import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import PerformerDropdownIndicator from "./PerformerDropdownIndicator";
import { useGetUserCompanies } from "../../../../backend/query";
import { FULL_USER_COMPANIES_PAYLOAD } from "../../../../backend/license";
import { useAssociateCompanyLogo } from "../../../../contexts/AssociateCompanyContext";

interface PerformerFiltersProps {
  onChange: (filters: any) => void;
  establishmentType: string;
  onEstablishmentTypeChange: (value: string) => void;
}

// Company always resolves to a concrete selection (first alphabetically,
// unless the user picks another) — see the auto-select effect below.
// Associate Company and Location both keep "All": Associate Company starts
// at "All" and only moves to a specific AC on a manual pick, showing the
// parent company's own branding while "All" via setAssociateCompanyBranding
// — see AssociateCompanyContext.
const ALL_OPTION = {
  value: "all",
  label: "ALL",
  company: null,
  associate_company: null,
  location: null,
};

const ESTABLISHMENT_TYPES = [
  { value: "", label: "Select Establishment Type" },
  { value: "Factory", label: "Factory" },
  { value: "Establishment", label: "Establishment" },
  { value: "CLRA", label: "CLRA" },
];

function sortByLabel(list: any[]) {
  return [...list].sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

function PerformerFilters({
  onChange,
  establishmentType,
  onEstablishmentTypeChange,
}: PerformerFiltersProps) {
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();

  const [company, setCompany] = useState<any>(null);
  const [associateCompany, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([ALL_OPTION]);

  // Guards the one-time default company selection so a later refetch of
  // userCompanies (or any unrelated re-render) never clobbers a manual user choice.
  const hasAutoSelectedCompany = useRef(false);

  const { userCompanies, isFetching } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  // Build the sorted company list AND pick the default (first alphabetically) in
  // the SAME effect, straight from the fresh `userCompanies` data. Splitting
  // "build the list" and "pick the default" across two effects leaves a
  // one-render gap that can wrongly look like "loaded but empty" and flash the
  // default ezyCOMP logo (see AssociateCompanyContext).
  useEffect(() => {
    if (isFetching) return; // still resolving — leave everything untouched (logo stays undefined)

    if (!userCompanies || userCompanies.length === 0) {
      setCompanies([]);
      // The company fetch has genuinely finished empty — no company (and so no
      // associate company) will ever load, so it's safe to resolve the logo now.
      if (!hasAutoSelectedCompany.current) {
        setAssociateCompanyBranding(null);
        hasAutoSelectedCompany.current = true;
      }
      return;
    }

    const companyOptions = sortByLabel(
      userCompanies.map(function (c: any) {
        return { value: c.id, label: c.name, company: c };
      })
    );
    setCompanies(companyOptions);

    if (!hasAutoSelectedCompany.current) {
      setCompany(companyOptions[0]);
      hasAutoSelectedCompany.current = true;
    }
  }, [userCompanies, isFetching]);

  function handleCompanyChange(opt: any) {
    hasAutoSelectedCompany.current = true; // manual pick locks out any further auto-select
    setCompany(opt);
  }

  // Build the associate-company list in the SAME effect as resetting the
  // selection to "All", straight from `company.company.associate_companies` —
  // that data is already fully loaded (nested in the same userCompanies fetch),
  // so there's no separate "still loading" state to guard against here.
  // Associate Company no longer auto-selects a specific AC — it resets to
  // "All", which shows the parent company's own branding (company.company is
  // the full raw record from the same tree fetch, so it already carries
  // logo/favicon/whitelabeling_enabled/parent_company_id).
  useEffect(() => {
    if (!company || !company.company) {
      setAssociateCompanies([]);
      setAssociateCompany(ALL_OPTION);
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
      return;
    }

    const acOptions = sortByLabel(
      (company.company.associate_companies || []).map(function (ac: any) {
        return {
          value: ac.id,
          label: ac.name,
          associate_company: ac,
          locations: ac.locations || [],
          logo: ac.logo,
          favicon: ac.favicon,
          whitelabeling_enabled: ac.whitelabeling_enabled,
        };
      })
    );
    setAssociateCompanies([ALL_OPTION, ...acOptions]);
    setAssociateCompany(ALL_OPTION);
    setAssociateCompanyBranding(company.company);
  }, [company]);

  function handleAssociateCompanyChange(opt: any) {
    const selected = opt || ALL_OPTION;
    setAssociateCompany(selected);
    if (selected.associate_company) {
      setAssociateCompanyBranding(selected.associate_company);
    } else {
      // "All" (re-)selected — show the parent company's own branding.
      setAssociateCompanyBranding(company && company.company ? company.company : null);
    }
  }

  useEffect(() => {
    if (associateCompany && associateCompany.locations) {
      const locationOptions = [
        ALL_OPTION,
        ...associateCompany.locations.map(function (loc: any) {
          const label =
            loc.city && loc.city.name
              ? loc.name + ", " + loc.city.name
              : loc.name;
          return { value: loc.id, label: label, location: loc };
        }),
      ];
      setLocations(locationOptions);
      setLocation(ALL_OPTION);
    } else {
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
    }
  }, [associateCompany]);

  useEffect(() => {
    const filters: any = {};

    if (company && company.value) {
      filters.company_id = company.value;
    }

    if (associateCompany && associateCompany.value && associateCompany.value !== "all") {
      filters.associate_company_id = associateCompany.value;
    }

    if (location && location.value !== "all") {
      filters.location_id = location.value;
    }

    onChange(filters);
  }, [company, associateCompany, location]);


  return (
    <>
      <div className="px-2">
        <label className="filter-label">
          <small>Establishment Type</small>
        </label>
        <Select
          placeholder="Establishment Type"
          options={ESTABLISHMENT_TYPES}
          onChange={(option: any) => onEstablishmentTypeChange(option.value)}
          value={ESTABLISHMENT_TYPES.find(opt => opt.value === establishmentType)}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Company</small>
        </label>
        <Select
          placeholder="Company"
          options={companies}
          onChange={handleCompanyChange}
          value={company}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
          isLoading={isFetching}
          isDisabled={!establishmentType}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Associate Company</small>
        </label>
        <Select
          placeholder="Associate Company"
          options={associateCompanies}
          onChange={handleAssociateCompanyChange}
          value={associateCompany}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
          isDisabled={!company || associateCompanies.length === 0}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Location</small>
        </label>
        <Select
          placeholder="Location"
          options={locations}
          onChange={setLocation}
          value={location}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
          isDisabled={!associateCompany || associateCompany.value === "all"}
        />
      </div>
    </>
  );
}

export default PerformerFilters;
